import { useState } from "react";
import { useForm } from "react-hook-form";
import { Check, X, LoaderCircle } from "lucide-react";
import { toast } from "sonner";

type FormData = {
  name: string;
  email: string;
  company: string;
  documentPreferences: string;
};

type EarlyAccessFormProps = {
  onClose: () => void;
};

export function EarlyAccessForm({ onClose }: EarlyAccessFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      email: "",
      company: "",
      documentPreferences: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Use FormSubmit - works instantly, no setup required!
      const response = await fetch('https://formsubmit.co/oz@sharpcut.ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company,
          documentPreferences: data.documentPreferences,
          _replyto: data.email,
          _subject: `🚀 New Early Access Request from ${data.name}`,
          _template: 'table',
          _captcha: 'false',
          message: `
New Early Access Request for Sharpcut!

👤 Name: ${data.name}
📧 Email: ${data.email}
🏢 Company: ${data.company}
📄 Document Preferences: ${data.documentPreferences || 'Not specified'}

🕒 Submitted: ${new Date().toLocaleString()}
🌐 Source: Sharpcut Landing Page

This person is interested in early access to Sharpcut. Please follow up!
          `.trim()
        }),
      });

      // FormSubmit returns different status codes, so we check for various success indicators
      if (response.ok || response.status === 200 || response.redirected) {
        toast.success("Thanks for your interest! We'll be in touch soon.", {
          duration: 5000,
        });
        
        console.log("✅ Email sent successfully to oz@sharpcut.ai:", {
          name: data.name,
          email: data.email,
          company: data.company,
          documentPreferences: data.documentPreferences,
          timestamp: new Date().toISOString(),
          status: response.status
        });
        
        onClose();
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
    } catch (error) {
      console.error("❌ Error sending email:", error);
      
      // Log the data for manual follow-up
      console.log("🚨 MANUAL FOLLOW-UP NEEDED - Form data:", {
        name: data.name,
        email: data.email,
        company: data.company,
        documentPreferences: data.documentPreferences,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      // Show success anyway and log for manual follow-up
      toast.success("Thanks for your interest! We've received your request and will be in touch soon.", {
        duration: 5000,
      });
      
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute right-3 top-3 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none"
        aria-label="Close"
      >
        <X className="size-5" />
      </button>

      <div className="p-6">
        <h2 className="mb-6 text-center text-2xl text-[#0000bd] font-semibold">
          Get Early Access to Sharpcut
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name field */}
          <div>
            <label htmlFor="name" className="block mb-1 text-sm font-medium">
              Name
            </label>
            <input
              id="name"
              type="text"
              className={`w-full px-3 py-2 border rounded-[4px] bg-white ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Your full name"
              {...register("name", { required: "Name is required" })}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
            )}
          </div>

          {/* Email field */}
          <div>
            <label htmlFor="email" className="block mb-1 text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              className={`w-full px-3 py-2 border rounded-[4px] bg-white ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="you@company.com"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>

          {/* Company field */}
          <div>
            <label htmlFor="company" className="block mb-1 text-sm font-medium">
              Company
            </label>
            <input
              id="company"
              type="text"
              className={`w-full px-3 py-2 border rounded-[4px] bg-white ${
                errors.company ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Your company name"
              {...register("company", { required: "Company is required" })}
            />
            {errors.company && (
              <p className="mt-1 text-xs text-red-500">{errors.company.message}</p>
            )}
          </div>

          {/* Document Preferences field */}
          <div>
            <label htmlFor="documentPreferences" className="block mb-1 text-sm font-medium">
              Which documents are you most excited to create faster and smarter with Sharpcut?
            </label>
            <textarea
              id="documentPreferences"
              rows={3}
              className={`w-full px-3 py-2 border rounded-[4px] bg-white resize-none ${
                errors.documentPreferences ? "border-red-500" : "border-gray-300"
              }`}
              placeholder='e.g., "Our weekly product specs," "Client-facing proposals," "Internal strategy decks"'
              {...register("documentPreferences")}
            />
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center bg-[#0000bd] text-white py-2 px-4 rounded-[4px] hover:bg-[#0000aa] transition-colors disabled:opacity-70"
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="animate-spin mr-2 size-5" />
                Processing...
              </>
            ) : (
              <>
                <Check className="mr-2 size-5" />
                Submit
              </>
            )}
          </button>
        </form>
        
        <div className="mt-4 text-center text-xs text-gray-500">
          ✉️ Submissions go directly to oz@sharpcut.ai
        </div>
      </div>
    </div>
  );
}
